/**
 * Breakpoint-aware background media, shared by the elements that show one
 * image-or-video per breakpoint (desktop = 1200px and up, mobile below).
 *
 * Only the video belonging to the active breakpoint is ever loaded, so a phone
 * never downloads the desktop file. Whether a video may play at all is the
 * subclass's decision (see `shouldPlay`): the banner autoplays, the about-supply
 * cards wait for a click.
 *
 * Markup contract: each video carries data-media-video="desktop" | "mobile".
 * The two must be siblings; CSS decides which one is displayed.
 */
class EuroMediaVideo extends HTMLElement {
  connectedCallback() {
    this.mq = window.matchMedia('(min-width: 1200px)');
    this.videos = Array.from(this.querySelectorAll('[data-media-video]'));

    this.onMediaChange = () => this.syncMedia();
    this.onVideoReady = (event) => {
      const video = event.currentTarget;
      if (this.shouldPlay(video)) this.playVideo(video);
    };

    this.mq.addEventListener('change', this.onMediaChange);

    this.videos.forEach((video) => {
      video.addEventListener('loadeddata', this.onVideoReady);
      video.addEventListener('canplay', this.onVideoReady);
    });

    this.syncMedia();
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.onMediaChange);

    this.videos?.forEach((video) => {
      video.removeEventListener('loadeddata', this.onVideoReady);
      video.removeEventListener('canplay', this.onVideoReady);
      video.pause();
    });
  }

  isDesktop() {
    return this.mq.matches;
  }

  /** Belongs this video to the breakpoint currently being displayed? */
  isActiveVideo(video) {
    const key = this.isDesktop() ? 'desktop' : 'mobile';
    return video.dataset.mediaVideo === key;
  }

  /**
   * Playback policy, per video. Subclasses decide: autoplay, or only after a
   * click. Returning false also means the file is never fetched.
   */
  shouldPlay() {
    return false;
  }

  /** Make every video match its intended state (called on mount + breakpoint change). */
  syncMedia() {
    this.videos.forEach((video) => {
      if (this.shouldPlay(video)) {
        this.playVideo(video);
      } else {
        this.releaseVideo(video);
      }
    });
  }

  /** Fetch the file (preload is held at "none" until now) and start playing. */
  playVideo(video) {
    if (video.preload !== 'auto') {
      video.preload = 'auto';
      if (video.readyState < 2) video.load();
    }

    const promise = video.play();
    if (promise?.catch) {
      promise.catch(() => {});
    }
  }

  /** Pause and let the file go so the download is not kept alive. */
  releaseVideo(video) {
    video.pause();
    video.preload = 'none';
  }
}

/** Homepage hero: hidden background video, plays as soon as it is loaded. */
class EuroBanner extends EuroMediaVideo {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = () => this.syncMedia();
    this.reducedMotion.addEventListener('change', this.onMotionChange);

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    super.disconnectedCallback();
  }

  shouldPlay(video) {
    return this.isActiveVideo(video) && !this.reducedMotion.matches;
  }
}

/**
 * About-supply cards: the visitor starts playback by clicking the play button
 * drawn over the poster, never automatically.
 */
class EuroAboutSupply extends EuroMediaVideo {
  connectedCallback() {
    /** Videos the visitor has started; everything else stays behind its poster. */
    this.started = new Set();

    this.onClick = (event) => this.handleClick(event);
    this.onPlaybackChange = () => this.syncCardUi();

    this.addEventListener('click', this.onClick);

    super.connectedCallback();

    this.videos.forEach((video) => {
      video.addEventListener('play', this.onPlaybackChange);
      video.addEventListener('pause', this.onPlaybackChange);
      video.addEventListener('ended', this.onPlaybackChange);
    });

    this.syncCardUi();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);

    this.videos?.forEach((video) => {
      video.removeEventListener('play', this.onPlaybackChange);
      video.removeEventListener('pause', this.onPlaybackChange);
      video.removeEventListener('ended', this.onPlaybackChange);
    });

    super.disconnectedCallback();
  }

  /** A breakpoint change drops the cards back to their poster. */
  syncMedia() {
    this.started?.clear();
    super.syncMedia();
    this.syncCardUi?.();
  }

  shouldPlay(video) {
    return this.isActiveVideo(video) && this.started.has(video);
  }

  /** The video of the breakpoint currently on screen, inside one card's media box. */
  activeVideoIn(media) {
    const key = this.isDesktop() ? 'desktop' : 'mobile';
    return media.querySelector(`[data-media-video="${key}"]`);
  }

  handleClick(event) {
    const media = event.target.closest?.('.euro-about-supply__media');
    if (!media || !this.contains(media)) return;

    // Must be the video of the visible breakpoint: a card can hold both a
    // desktop and a mobile file, and only the displayed one is clickable.
    const video = this.activeVideoIn(media);
    if (!video) return;

    if (!this.started.has(video) || video.paused) {
      // Starting inside the click handler keeps the user gesture, which is what
      // lets the browser play at all.
      this.started.add(video);
      this.playVideo(video);
    } else {
      video.pause();
    }

    this.syncCardUi();
  }

  /** The play button is only shown while its video is not running. */
  syncCardUi() {
    this.querySelectorAll('.euro-about-supply__media').forEach((media) => {
      const video = this.activeVideoIn(media);
      const playing = video ? !video.paused && !video.ended : false;
      media.classList.toggle('is-playing', playing);
    });
  }
}

// Guarded: both the banner and the supply section can load this asset on the
// same page.
if (!customElements.get('euro-banner')) {
  customElements.define('euro-banner', EuroBanner);
}

if (!customElements.get('euro-about-supply')) {
  customElements.define('euro-about-supply', EuroAboutSupply);
}
